import type { Auth, User } from 'firebase/auth'

type LazyFirebase = typeof import('./lazy-firebase')

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  databaseURL: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string
}

export let firebaseConfig: FirebaseConfig | undefined
export function setFirebaseConfig(config: FirebaseConfig): void {
  firebaseConfig = config
}

async function importFirebase(): Promise<LazyFirebase> {
  return import(/* webpackChunkName: "firebase", webpackMode: "lazy" */ './lazy-firebase')
}

export async function getAuth(): Promise<Auth> {
  const { lazyGetAuth } = await importFirebase()
  return lazyGetAuth()
}

export async function getIdToken(user: User, forceRefresh?: boolean): Promise<string> {
  const { lazyGetIdToken } = await importFirebase()
  return lazyGetIdToken(user, forceRefresh)
}

export async function signInWithFacebook(domain: string): Promise<User> {
  const { lazySignInWithFacebook } = await importFirebase()
  return lazySignInWithFacebook(domain)
}

export async function signInWithEmailAndPassword({
  email,
  password
}: {
  email: string
  password: string
}): Promise<User> {
  const { lazySignInWithEmailAndPassword } = await importFirebase()
  return lazySignInWithEmailAndPassword({ email, password })
}

export async function signOut(): Promise<void> {
  const { lazySignOut } = await importFirebase()
  return lazySignOut()
}

export async function getUser(): Promise<User | null> {
  const { lazyGetUser } = await importFirebase()
  return lazyGetUser()
}

export async function updateUserName(name: string): Promise<void> {
  const { lazyUpdateUserName } = await importFirebase()
  return lazyUpdateUserName(name)
}

export async function reauthenticateWithEmailAndPassword({
  email,
  password
}: {
  email: string
  password: string
}): Promise<void> {
  const { lazyReauthenticateWithEmailAndPassword } = await importFirebase()
  return lazyReauthenticateWithEmailAndPassword({ email, password })
}

export async function updatePassword(password: string): Promise<void> {
  const { lazyUpdatePassword } = await importFirebase()
  return lazyUpdatePassword(password)
}
